import type { CSSProperties } from "react";

/**
 * Habillage de cette visite. Volontairement minuscule, et pour l'instant
 * entièrement neutre : le moteur fournit des styles sobres, et tout ce qui
 * relèvera de l'identité du château viendra s'ajouter ICI — pas en dur dans
 * les composants.
 *
 * Les familles sont déclarées dans `typography.css` et lues ici par variables
 * CSS : une seule source pour le choix des caractères.
 */

/** Interface : menus, panneaux, boutons, messages. */
export const POLICE_UI = "var(--police-ui)";

/** Annonce du nom de scène, à l'arrivée dans un lieu. */
export const POLICE_TITRE = "var(--police-titre)";

/**
 * L'en-tête au logo (`Bandeau.tsx`) : hauteur du dégradé, et hauteur du logo.
 * Le logo est fluide, mais un téléphone ne le réduit plus qu'à peine : à 72 px
 * le nom écrit sous le blason se lisait mal, et le haut de l'écran a la place
 * (l'accueil et les langues n'occupent que les coins). Agrandi de 30 % sur
 * téléphone (ancien 100 px / 26 vw / 132 px) : plancher à 130 px, soit environ
 * 156 px de large — à vérifier qu'il tient entre les deux boutons à 360 px.
 */
export const ENTETE_HAUTEUR = 220;
export const ENTETE_LOGO = "clamp(130px, 33.8vw, 172px)";
/**
 * Sur ordinateur, 30 % au-dessus du plafond téléphone (132 px) : sur un grand
 * écran, le logo paraissait petit dans un en-tête de 220 px qu'il laisse vide.
 */
export const ENTETE_LOGO_ORDINATEUR = "220px";

export const toggleStyle: CSSProperties = {
  position: "absolute",
  bottom: 12,
  right: 12,
  padding: "5px 9px",
  borderRadius: 6,
  border: "1px solid #3a3a3a",
  background: "rgba(17,17,17,0.72)",
  color: "#ccc",
  fontSize: 13,
  textDecoration: "none",
  pointerEvents: "auto",
};

/** Écrans de chargement et d'erreur : plein cadre, centré, sobre. */
export const centeredStyle: CSSProperties = {
  position: "absolute",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  padding: 24,
  background: "#111",
  color: "#ccc",
  fontFamily: POLICE_UI,
  fontSize: 14,
  lineHeight: 1.5,
  textAlign: "center",
};

/**
 * Témoin de provenance des tuiles. Sans lui, rien ne distingue à l'écran une
 * visite servie par le disque d'une visite servie par R2 — et c'est précisément
 * ce qu'on cherche à vérifier.
 */
export const sourceBadgeStyle: CSSProperties = {
  position: "absolute",
  top: 12,
  left: "50%",
  transform: "translateX(-50%)",
  padding: "3px 10px",
  borderRadius: 999,
  border: "1px solid",
  background: "rgba(0,0,0,0.55)",
  fontFamily: POLICE_UI,
  fontSize: 11,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  textDecoration: "none",
  pointerEvents: "auto",
};

/**
 * Trois états, trois tons — et le gris compte autant que les deux autres.
 *
 * `expected` : tout vient de l'origine attendue. `mixed` : une tuile au moins
 * vient d'ailleurs, ce qui arrive quand un document publié référence des clés
 * dont les voisines sont restées locales. `silent` : rien n'a été demandé —
 * scène sans panorama, ou tuiles que le serveur n'a jamais servies.
 */
export const sourceBadgeTones: Record<"expected" | "mixed" | "silent", CSSProperties> = {
  expected: { borderColor: "#7cf", color: "#7cf" },
  mixed: { borderColor: "#fc7", color: "#fc7" },
  silent: { borderColor: "#555", color: "#888" },
};
